import { useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SettlementsContext } from '@/modules/Settlements/context/context';
import { ISettlements } from '@/modules/Settlements/types';
import { useGetWorkersList } from '@/modules/workingDayReconciliation/apiHooks';
import { WorkersSuggestionsList } from '@/modules/workingDayReconciliation/components/Header/WorkersSuggestionsList';
import { WorkingDayReconciliationContext } from '@/modules/workingDayReconciliation/context/context';
import { workingDayReconciliationActionsTypes as ActionsTypes } from '@/modules/workingDayReconciliation/context/reducer';
import DemoCTA from '@/shared/components/atoms/Demo/Modals/DemoCTA';
import Search from '@/shared/components/atoms/Search';
import { useStore } from '@/shared/hooks/context/createGenericContext';
import { useModal } from '@/shared/hooks/context/modalContext/useModal';

const WorkerSearch: React.FC<{
  type: 'settlement' | 'daily-due';
}> = ({ type }) => {
  const { t } = useTranslation('common');
  const { openModal } = useModal();
  const divRef = useRef<HTMLDivElement>(null);
  const [showList, setShowList] = useState<boolean>(true);
  const [selectedAsset] = useStore((store) => store?.asset);
  const [isDemoFarm] = useStore((store) => store?.isDemoFarm);
  const context =
    type === 'daily-due' ? WorkingDayReconciliationContext : SettlementsContext;
  const {
    state: { search, workerId },
    dispatch,
  } = useContext(context as typeof WorkingDayReconciliationContext);
  const queryClient = useQueryClient();

  const workersList = useGetWorkersList({
    entityId: selectedAsset?.id,
    search,
  });

  const handleWorkerSelection = (worker?: ISettlements.IWorkerInfo) => {
    dispatch({
      payload: worker?.workerId,
      type: ActionsTypes.setWorkerId,
    });
    dispatch({ payload: worker?.workerName, type: ActionsTypes.setSearch });
    setShowList(false);
  };

  useEffect(() => {
    if (search && search.length > 1 && !workerId) {
      setShowList(true);
    }
  }, [search, workerId]);

  return (
    <div className='relative' ref={divRef}>
      <Search
        onSearch={(keyword) => {
          if (keyword !== search) {
            dispatch({
              payload: undefined,
              type: ActionsTypes.setWorkerId,
            });
          }
          dispatch({
            payload: keyword,
            type: ActionsTypes.setSearch,
          });
        }}
        placeholder={t('search-by-worker')}
        onClick={isDemoFarm ? () => openModal(<DemoCTA />) : undefined}
        className={clsx(
          isDemoFarm && 'pointer-events-none',
          search && !workerId && '!rounded-b-[0px]'
        )}
        px='px-12'
        py='py-16'
        resetFn={() => {
          handleWorkerSelection();
          queryClient.invalidateQueries(['getWorkerSettlements']);
          queryClient.invalidateQueries(['workerDailyDuesSummary']);
        }}
        searchKeyword={search}
      />
      {search && showList && !workerId && (
        <WorkersSuggestionsList
          requestInfo={workersList}
          divRef={divRef}
          setWorkerIdValue={handleWorkerSelection}
        />
      )}
    </div>
  );
};
export { WorkerSearch };
